import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getTour } from "../tours";

const TourContext = createContext(null);

const STORAGE_KEY = "academia_tour_state";

export function TourProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // Persist so tour survives route transitions.
  useEffect(() => {
    if (state) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else localStorage.removeItem(STORAGE_KEY);
  }, [state]);

  const tour = state ? getTour(state.slug) : null;
  const step = tour ? tour.steps[state.index] : null;

  // On each step change, if the step has a route different from current, navigate.
  useEffect(() => {
    if (!step) return;
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [step, location.pathname, navigate]);

  const start = useCallback((slug) => {
    const t = getTour(slug);
    if (!t) return;
    setState({ slug, index: 0 });
  }, []);

  const next = useCallback(() => {
    setState((prev) => {
      if (!prev) return prev;
      const t = getTour(prev.slug);
      if (!t) return null;
      if (prev.index + 1 >= t.steps.length) return null; // finished
      return { ...prev, index: prev.index + 1 };
    });
  }, []);

  const back = useCallback(() => {
    setState((prev) => {
      if (!prev || prev.index === 0) return prev;
      return { ...prev, index: prev.index - 1 };
    });
  }, []);

  const stop = useCallback(() => setState(null), []);

  const value = useMemo(() => ({
    active: !!state,
    tour,
    step,
    index: state?.index ?? 0,
    total: tour?.steps.length ?? 0,
    start,
    next,
    back,
    stop,
  }), [state, tour, step, start, next, back, stop]);

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be inside <TourProvider>");
  return ctx;
}
